import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ExternalLinkIcon, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import cacheKeys from "@/config/cache-keys";
import pages from "@/config/pages";
import { computeProductDrift } from "@/lib/insuranceDrift";
import {
	emitSyncDisabledDriftResultToasts,
	syncDisabledDriftFromTrees,
} from "@/lib/syncDisabledProductDrift";
import AppService from "@/services/AppService";
import type { APIResponse } from "@/types/APIresponse";
import type { Account } from "@/types/account/account";
import type { AccountTree } from "@/types/accountTree/accountTree";
import { productDefinitionsToTemplates } from "@/types/insurance/productDefinition";

const appService = new AppService();

interface Props {
	open: boolean;
	onClose: () => void;
	productType: string;
	productName: string;
	/** Called once per fan-out completion with drift counts for every product type. */
	onDriftResolved?: (countByType: Map<string, number>) => void;
}

export const ProductDriftModal = ({
	open,
	onClose,
	productType,
	productName,
	onDriftResolved,
}: Props) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [isSyncingDisabled, setIsSyncingDisabled] = useState(false);
	const [unsafeSyncAcknowledged, setUnsafeSyncAcknowledged] = useState(false);

	useEffect(() => {
		if (!open) {
			setUnsafeSyncAcknowledged(false);
		}
	}, [open]);

	// ── 1. All child accounts (gives us unique main_account_ids) ─────────────
	const { data: accountsData, isLoading: isLoadingAccounts } = useQuery<
		APIResponse<Account[]>
	>({
		queryKey: ["accounts", "all"],
		queryFn: () => appService.getAllAccounts(),
		enabled: open,
		staleTime: 1000 * 60 * 5,
	});

	const mainAccountIds = useMemo<string[]>(() => {
		if (!accountsData?.Result) return [];
		const seen = new Set<string>();
		const ids: string[] = [];
		for (const acc of accountsData.Result) {
			if (!seen.has(acc.main_account_id)) {
				seen.add(acc.main_account_id);
				ids.push(acc.main_account_id);
			}
		}
		return ids;
	}, [accountsData]);

	// ── 2. Fan-out: one tree request per main account ─────────────────────────
	const treeQueries = useQueries({
		queries: mainAccountIds.map((id) => ({
			queryKey: cacheKeys.SINGLE_MAIN_ACCOUNT(id),
			queryFn: async () => appService.getMainAccountTree(id),
			enabled: open && mainAccountIds.length > 0,
			staleTime: 1000 * 60 * 5,
		})),
	});

	// ── 3. Product-definition templates ───────────────────────────────────────
	const { data: definitionsData } = useQuery({
		queryKey: cacheKeys.INSURANCE_PRODUCT_DEFINITIONS,
		queryFn: async () => {
			const res = await appService.getInsuranceProductDefinitions();
			return res.Result ?? [];
		},
		staleTime: 1000 * 60 * 60,
		enabled: open,
	});

	const templates = useMemo(
		() =>
			definitionsData ? productDefinitionsToTemplates(definitionsData) : [],
		[definitionsData],
	);

	const isLoadingTrees =
		treeQueries.length > 0 && treeQueries.some((q) => q.isLoading);
	const isLoading =
		isLoadingAccounts || isLoadingTrees || templates.length === 0;

	const treesFromQueries = useMemo(
		() =>
			treeQueries
				.map((q) => q.data?.Result)
				.filter((t): t is AccountTree => Boolean(t)),
		[treeQueries],
	);

	type DriftInstance = {
		mainAccountId: string;
		mainAccountName: string;
		missingParams: string[];
		extraParams: string[];
	};

	const driftInstances = useMemo<DriftInstance[]>(() => {
		if (isLoading) return [];
		const results: DriftInstance[] = [];
		for (const q of treeQueries) {
			const tree = q.data?.Result;
			if (!tree) continue;
			const report = computeProductDrift(tree.insurances, templates);
			const match = report.drifted.find((d) => d.type === productType);
			if (match) {
				results.push({
					mainAccountId: tree.main_account.main_account_id,
					mainAccountName: tree.main_account.display_name,
					missingParams: match.missingParams,
					extraParams: match.extraParams,
				});
			}
		}
		return results;
	}, [treeQueries, templates, productType, isLoading]);

	const syncableDrift = useMemo(() => {
		if (isLoading || templates.length === 0) {
			return { accountCount: 0, rowCount: 0 };
		}
		let accountCount = 0;
		let rowCount = 0;
		for (const q of treeQueries) {
			const tree = q.data?.Result;
			if (!tree) continue;
			const report = computeProductDrift(tree.insurances, templates);
			const driftedIds = new Set(
				report.drifted.filter((d) => d.type === productType).map((d) => d.id),
			);
			const rows = tree.insurances.filter(
				(ins) =>
					ins.type === productType &&
					driftedIds.has(ins.id) &&
					(ins.groups?.length ?? 0) === 0,
			);
			if (rows.length > 0) {
				accountCount += 1;
				rowCount += rows.length;
			}
		}
		return { accountCount, rowCount };
	}, [isLoading, treeQueries, templates, productType]);

	const allDriftSyncable = useMemo(() => {
		if (isLoading || templates.length === 0) {
			return { accountCount: 0, rowCount: 0 };
		}
		let accountCount = 0;
		let rowCount = 0;
		for (const q of treeQueries) {
			const tree = q.data?.Result;
			if (!tree) continue;
			const report = computeProductDrift(tree.insurances, templates);
			const entries = report.drifted.filter((d) => d.type === productType);
			if (entries.length > 0) {
				accountCount += 1;
				rowCount += entries.length;
			}
		}
		return { accountCount, rowCount };
	}, [isLoading, treeQueries, templates, productType]);

	const runCatalogDriftSync = useCallback(
		async (onlyRowsWithoutGroups: boolean) => {
			const template = templates.find((def) => def.type === productType);
			if (!template) {
				toast.error(
					t("admin.product-definitions.drift-modal.sync-no-template"),
				);
				return;
			}
			if (onlyRowsWithoutGroups) {
				if (syncableDrift.accountCount === 0) return;
			} else {
				if (!unsafeSyncAcknowledged) return;
				if (allDriftSyncable.rowCount === 0) return;
			}

			setIsSyncingDisabled(true);
			try {
				const { updatedAccounts, failedAccounts } =
					await syncDisabledDriftFromTrees({
						productType,
						templates,
						trees: treesFromQueries,
						appService,
						queryClient,
						onlyRowsWithoutGroups,
					});
				emitSyncDisabledDriftResultToasts(t, updatedAccounts, failedAccounts);
			} finally {
				setIsSyncingDisabled(false);
			}
		},
		[
			templates,
			productType,
			treesFromQueries,
			queryClient,
			t,
			syncableDrift.accountCount,
			allDriftSyncable.rowCount,
			unsafeSyncAcknowledged,
		],
	);

	useEffect(() => {
		if (isLoading || !onDriftResolved || treeQueries.length === 0) return;
		const countByType = new Map<string, number>();
		for (const q of treeQueries) {
			const tree = q.data?.Result;
			if (!tree) continue;
			const report = computeProductDrift(tree.insurances, templates);
			for (const d of report.drifted) {
				countByType.set(d.type, (countByType.get(d.type) ?? 0) + 1);
			}
		}
		onDriftResolved(countByType);
	}, [isLoading, treeQueries, templates, onDriftResolved]);

	const completedCount = treeQueries.filter(
		(q) => q.isSuccess || q.isError,
	).length;
	const totalCount = mainAccountIds.length;

	const showSyncChrome = !isLoading && driftInstances.length > 0;

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{t("admin.product-definitions.drift-modal.title", {
							product: productName,
						})}
					</DialogTitle>
					<DialogDescription>
						{t("admin.product-definitions.drift-modal.description")}
					</DialogDescription>
				</DialogHeader>

				{isLoading ? (
					<div className="flex flex-col items-center gap-3 py-8">
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						<p className="text-sm text-muted-foreground">
							{totalCount > 0
								? t("admin.product-definitions.drift-modal.loading-progress", {
										done: completedCount,
										total: totalCount,
									})
								: t("admin.product-definitions.drift-modal.loading")}
						</p>
					</div>
				) : driftInstances.length === 0 ? (
					<div className="flex flex-col items-center gap-2 py-8 text-center">
						<div className="rounded-full bg-moss-green-80/10 p-3">
							<AlertCircle className="h-5 w-5 text-moss-green-80" />
						</div>
						<p className="text-sm font-medium">
							{t("admin.product-definitions.drift-modal.no-drift")}
						</p>
						<p className="text-xs text-muted-foreground">
							{t("admin.product-definitions.drift-modal.no-drift-description")}
						</p>
					</div>
				) : (
					<div className="space-y-2">
						<p className="text-xs text-muted-foreground">
							{t("admin.product-definitions.drift-modal.count", {
								count: driftInstances.length,
							})}
						</p>
						<ul className="divide-y divide-border rounded-md border max-h-[60vh] overflow-y-auto">
							{driftInstances.map((instance) => (
								<li key={instance.mainAccountId}>
									<Link
										to={pages.AdminMainAccountUpdate(instance.mainAccountId)}
										onClick={onClose}
										className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors"
									>
										<div className="flex flex-col gap-1 min-w-0">
											<span className="font-medium truncate">
												{instance.mainAccountName}
											</span>
											<div className="flex flex-wrap gap-1">
												{instance.missingParams.map((p) => (
													<Badge
														key={p}
														variant="secondary"
														className="text-xs font-normal"
													>
														+ {p}
													</Badge>
												))}
												{instance.extraParams.map((p) => (
													<Badge
														key={p}
														variant="destructive"
														className="text-xs font-normal"
													>
														− {p}
													</Badge>
												))}
											</div>
										</div>
										<ExternalLinkIcon className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
									</Link>
								</li>
							))}
						</ul>
					</div>
				)}

				{showSyncChrome ? (
					<DialogFooter className="flex-col items-stretch gap-3 sm:flex-col sm:space-x-0">
						<p className="text-xs text-muted-foreground text-left w-full">
							{t("admin.product-definitions.drift-modal.sync-disabled-hint")}
						</p>
						{syncableDrift.accountCount === 0 && driftInstances.length > 0 ? (
							<p className="text-xs text-amber-800 dark:text-amber-200/90 text-left w-full">
								{t(
									"admin.product-definitions.drift-modal.sync-disabled-none-available",
								)}
							</p>
						) : null}
						{driftInstances.length > 0 && !unsafeSyncAcknowledged ? (
							<div className="space-y-2 rounded-md border border-amber-200/80 bg-amber-50/80 p-3 text-left dark:border-amber-900/60 dark:bg-amber-950/40">
								<p className="text-xs text-amber-950 dark:text-amber-100/90">
									{t(
										"admin.product-definitions.drift-modal.sync-risk-explanation",
									)}
								</p>
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="w-full border-amber-800/40 bg-white hover:bg-amber-100/80 dark:bg-zinc-900 dark:hover:bg-zinc-800"
									onClick={() => setUnsafeSyncAcknowledged(true)}
								>
									{t(
										"admin.product-definitions.drift-modal.sync-acknowledge-risk-button",
									)}
								</Button>
							</div>
						) : null}
						{unsafeSyncAcknowledged ? (
							<div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-left">
								<p className="text-xs font-medium text-destructive">
									{t(
										"admin.product-definitions.drift-modal.sync-risk-unlocked-banner",
									)}
								</p>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="h-8 shrink-0 text-xs"
									onClick={() => setUnsafeSyncAcknowledged(false)}
								>
									{t(
										"admin.product-definitions.drift-modal.sync-acknowledge-revoke",
									)}
								</Button>
							</div>
						) : null}
						<Button
							type="button"
							variant="secondary"
							disabled={
								isLoading ||
								isSyncingDisabled ||
								syncableDrift.accountCount === 0
							}
							onClick={() => void runCatalogDriftSync(true)}
							className="w-full sm:w-full"
						>
							{isSyncingDisabled ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{t("admin.product-definitions.drift-modal.sync-running")}
								</>
							) : (
								t(
									"admin.product-definitions.drift-modal.sync-disabled-button",
									{
										accounts: syncableDrift.accountCount,
										rows: syncableDrift.rowCount,
									},
								)
							)}
						</Button>
						{unsafeSyncAcknowledged && allDriftSyncable.rowCount > 0 ? (
							<Button
								type="button"
								variant="destructive"
								disabled={
									isLoading ||
									isSyncingDisabled ||
									allDriftSyncable.rowCount === 0
								}
								onClick={() => void runCatalogDriftSync(false)}
								className="w-full sm:w-full"
							>
								{isSyncingDisabled ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										{t("admin.product-definitions.drift-modal.sync-running")}
									</>
								) : (
									t(
										"admin.product-definitions.drift-modal.sync-all-drifted-button",
										{
											accounts: allDriftSyncable.accountCount,
											rows: allDriftSyncable.rowCount,
										},
									)
								)}
							</Button>
						) : null}
						{unsafeSyncAcknowledged ? (
							<p className="text-xs text-muted-foreground text-left">
								{t(
									"admin.product-definitions.drift-modal.sync-all-drifted-hint",
								)}
							</p>
						) : null}
					</DialogFooter>
				) : null}
			</DialogContent>
		</Dialog>
	);
};
